'use client';

import React, { useEffect, useState } from 'react';
import { LogoLink } from '@/components/shared/LogoLink';
import BuildingsSearchBar from '@/features/buildings/BuildingsSearchBar';
import { ListingsSearchBarMobile } from '@/features/listings/ListingsSearchBar.Mobile';

import { IconHome, IconReviewStars, IconSearch } from '@repo/svgs';
import { Tab } from '../ui';
import { BuildingsSearchBarMobile } from '@/features/buildings/BuildingsSearchBar.Mobile';
import { AiMiniSearchBar } from '@/features/ai-search/AiMiniSearchBar';
import { AISearchBarMobile } from '@/features/ai-search/AISearchBarMobile';
import { useAiSearchStatus } from '@/features/ai-search/hooks/useAiSearchStatus';
import { cn } from '@/lib/cn';

type HeaderType = 'default' | 'Mini search bar' | 'Expanded search';
type HeaderTab = 'Default' | 'Rent apartments' | 'Read reviews';

interface MobileSearchHeaderProps {
  type?: HeaderType;
  tab?: HeaderTab;
  onTabChange?: (tab: 'rent' | 'reviews') => void;
  onSearchClick?: () => void;
  /** Called whenever the rendered header height changes (useful for adding dynamic spacing below a fixed header). */
  onHeightChange?: (heightPx: number) => void;
  className?: string;
}

const SearchBarTabs = ({
  onTabClick,
  selectedTab,
}: {
  onTabClick: (tab: 'rent' | 'reviews') => void;
  selectedTab: 'rent' | 'reviews';
}) => {
  return (
    <div className="flex items-center justify-center animate-slide-down">
      <Tab
        background="secondary"
        active={selectedTab === 'rent'}
        icon={<IconHome />}
        onClick={() => onTabClick('rent')}
        className="flex-1"
      >
        Rent apartments
      </Tab>

      <Tab
        background="secondary"
        active={selectedTab === 'reviews'}
        icon={<IconReviewStars />}
        onClick={() => onTabClick('reviews')}
        className="flex-1"
      >
        Read reviews
      </Tab>
    </div>
  );
};

export function MobileSearchHeader({
  type = 'Mini search bar',
  tab = 'Rent apartments',
  onTabChange,
  onSearchClick,
  className,
}: MobileSearchHeaderProps) {
  const [showExpanded, setShowExpanded] = useState(type === 'Expanded search');
  const [aiOpen, setAiOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'rent' | 'reviews'>(
    tab === 'Rent apartments' ? 'rent' : 'reviews',
  );

  const aiStatus = useAiSearchStatus();
  // On rent pages, the AI full-page search replaces the legacy tabbed overlay
  // (unless the switch is disabled). Reviews pages keep the buildings flow.
  const aiRentEnabled = tab === 'Rent apartments' && aiStatus !== 'disabled';

  useEffect(() => {
    setSelectedTab(tab === 'Rent apartments' ? 'rent' : 'reviews');
  }, [tab]);

  const placeholder =
    selectedTab === 'rent' ? 'Start your apartment search' : 'Search address or building';

  const handleTabClick = (tab: 'rent' | 'reviews') => {
    setSelectedTab(tab);
    onTabChange?.(tab);
  };

  const handleClose = () => {
    setShowExpanded(false);
    setSelectedTab(tab === 'Rent apartments' ? 'rent' : 'reviews');
  };

  const handleSearchClick = () => {
    setShowExpanded(true);
    onSearchClick?.();
  };

  // Default type - just logo
  if (type === 'default') {
    return (
      <div
        data-oi-header
        className={cn(
          'md:hidden fixed top-0 z-30 bg-primary border-b border-secondary w-full h-[58px]',
          className,
        )}
      >
        <div className="h-full flex items-center justify-center px-6 py-4">
          <LogoLink variant="horizontal" />
        </div>
      </div>
    );
  }

  // Mini search bar type
  if (type === 'Mini search bar') {
    return (
      <>
        {!showExpanded && (
          <div
            data-oi-header
            className={cn(
              'md:hidden fixed top-0 z-30 bg-primary border-b border-secondary w-full h-[76px]',
              className,
            )}
          >
            <div className="h-full flex gap-4 items-end pl-6 pr-4 py-4">
              {/* Vertical logo - 60px width, ~37px height */}

              <LogoLink />

              {/* Search bar container */}
              <div className="flex-1 flex items-center min-w-0">
                {aiRentEnabled ? (
                  // Sparkle mini bar → opens the full-page AI overlay (same as
                  // mweb home). No rent/reviews tab switching in this flow.
                  <AiMiniSearchBar
                    variant="mobile"
                    className="flex-1"
                    onClick={() => setAiOpen(true)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={handleSearchClick}
                    className="flex-1 bg-primary border border-secondary flex gap-2 h-11 items-center min-w-0 px-4 py-1 rounded-full shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.06)]"
                  >
                    <IconSearch className="icon-tertiary w-4 h-4 shrink-0" />
                    <span className="text-left text-tertiary text-body-2 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {placeholder}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* AI full-page overlay (rent). The header mini bar above is the trigger,
            so the component renders only the overlay. */}
        {aiRentEnabled && (
          <AISearchBarMobile open={aiOpen} onOpenChange={setAiOpen} showTrigger={false} />
        )}

        {/* Expanded Search Overlay (legacy tabbed flow — reviews, or AI disabled) */}
        {!aiRentEnabled && showExpanded && (
          <>
            {/* Backdrop */}
            <div
              className="md:hidden fixed inset-0 z-40 bg-black/20"
              onClick={handleClose}
              aria-hidden="true"
            />

            {/* Search Overlay Card */}
            <div
              className={cn(
                'md:hidden fixed top-0 left-0 right-0 z-50 bg-primary border-b border-secondary shadow-lg animate-slide-down',
              )}
            >
              <div className="flex flex-col gap-2 items-center justify-center pb-6 pt-4">
                {/* Horizontal logo - 120px width, 26px height */}
                <div className="shrink-0">
                  <LogoLink variant="horizontal" />
                </div>

                {/* Tabs and Search Container */}
                <div className="flex flex-col gap-6 items-start w-full">
                  {/* Tabs */}
                  <div className="w-full">
                    <SearchBarTabs onTabClick={handleTabClick} selectedTab={selectedTab} />
                  </div>

                  {/* Search Content */}
                  <div className="w-full">
                    {selectedTab === 'rent' ? (
                      <ListingsSearchBarMobile />
                    ) : (
                      <div className="flex items-center justify-center">
                        <BuildingsSearchBarMobile className="w-full px-4" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // Expanded search type
  return (
    <div
      data-oi-header
      className={cn(
        'md:hidden fixed top-0 z-30 bg-primary border-b border-secondary w-full h-[194px]',
        className,
      )}
    >
      <div className="h-full flex flex-col gap-2 items-center justify-center pb-6 pt-4 px-6">
        {/* Horizontal logo - 120px width, 26px height */}
        <div className="shrink-0">
          <LogoLink variant="horizontal" />
        </div>

        {/* Tabs and Search Container */}
        <div className="flex flex-col gap-6 items-start w-full">
          {/* Tabs */}
          <div className="w-full">
            <SearchBarTabs onTabClick={handleTabClick} selectedTab={selectedTab} />
          </div>

          {/* Search Content */}
          <div className="w-full">
            {selectedTab === 'rent' ? (
              <ListingsSearchBarMobile />
            ) : (
              <div className="flex items-center justify-center">
                <BuildingsSearchBarMobile className="container-grid" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
